import React, { useContext, useEffect, useState } from 'react';
import { Option } from '../../models/Option';
import { DropdownItem } from './DropdownItem';
import { ReactComponent as Dropdown } from '../../assets/svgs/drop_down.svg';
import { ReactComponent as Close } from "../../assets/svgs/close-circle-ash.svg"; 
import { StyleContext } from '../../providers/StyleContextProvider';
import { useMediaQuery } from 'react-responsive';
import { merge } from 'react-merge';
import { MenuOption } from '../../models/MenuOption';

interface Props {
    styles?: any;
    label: string;
    menuOptions: MenuOption[];
    initialValue?: string;
    searchDisabled?: boolean;
    getSelectedValue(selectedValue: any): void;
}

export const MenuSelect: React.FC<Props> = (props) => {
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("select")); 
    const [primaryLabel, setPrimaryLabel] = useState<string>(props.label);
    const [secondaryLabel, setSecondaryLabel] = useState<string>();
    const [menuOptions, setMenuOptions] = useState<MenuOption[]>(props.menuOptions);
    const [filteredMenuOptions, setFilteredMenuOptions] = useState<MenuOption[]>(props.menuOptions);    
    const [hovered, setHovered] = useState<boolean>(false);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [resetEnable, setResetEnable] = useState<boolean>(false);

    useEffect(() => {
        if(props.initialValue) {
            setInitialValue();
        }
    }, []);

    useEffect(() => {
        setMenuOptions(props.menuOptions); 
        setFilteredMenuOptions(props.menuOptions);
    }, [props.menuOptions]);

    useEffect(() => {           
        let style = styleContext.getComponentStyle("select");
        let extendedStyle = merge(style, props.styles);  
        setStyles(extendedStyle);        
    }, [isMobile]);

    function filterOptions(keyword: string) {
        keyword = keyword.trim();        
        let filteredOptions = menuOptions.map(menuOption => ({
            labelId: menuOption.labelId,
            label: menuOption.label,
            expand: true,            
            options: menuOption.options.filter(option =>
                option.primaryOption.toLowerCase().includes(keyword.toLowerCase()) ||
                option.secondaryOption && option.secondaryOption.toLowerCase().includes(keyword.toLowerCase())
            )
        }));     
        filteredOptions = filteredOptions.filter(menuOption => menuOption.options.length > 0);   
        setFilteredMenuOptions(filteredOptions);
    }

    const setInitialValue = () => {                          
        menuOptions.forEach((menuOption) => {
            menuOption.options.forEach((option) => {                                                
                if(option.value == props.initialValue) {                     
                    setPrimaryLabel(option.primaryOption);
                    setSecondaryLabel(option.secondaryOption);
                    return;
                }
            });
        });
    }

    const selectOption = (option: Option) => {
        setPrimaryLabel(option.primaryOption);
        setSecondaryLabel(option.secondaryOption);
        props.getSelectedValue(option.value);
        setResetEnable(true);
    }

    const itemExpand = (selectedParentIndex:number) => {
        let tempMenuOptions = [...menuOptions];
        tempMenuOptions[selectedParentIndex].expand = !tempMenuOptions[selectedParentIndex].expand;
        setMenuOptions(tempMenuOptions);
    }

    const resetOption = (event: React.MouseEvent) => {
        event.stopPropagation();        
        setPrimaryLabel(props.label);
        setSecondaryLabel(undefined);
        props.getSelectedValue(undefined);
        setResetEnable(false);
    }

    return (
        <div
            style={styles.wrapper}
            onMouseEnter={() => {setHovered(true);}}
            onMouseLeave={() => {setHovered(false);}} 
        >
            <div style={styles.labelWrapper}>
                <div>
                    <div style={styles.label}>{primaryLabel}</div>
                    {secondaryLabel && <div style={styles.label}>{secondaryLabel}</div>}
                </div>
                <Dropdown style={{...styles.labelIcon, ...(hovered && styles.labelIcon.hovered)}} />
                {resetEnable && <Close style={styles.resetIcon} onClick={(e) => resetOption(e)} />}
            </div>
            <div style={hovered ? styles.hover : styles.leave}>
                {!props.searchDisabled && <input type="text" style={styles.search} placeholder={"Search"} onChange={(e) => filterOptions(e.target.value)}/>}
                <div style={styles.container} className={"selector"}>
                    {filteredMenuOptions.map((menuOption, parentIndex) => 
                        <div key={parentIndex}>
                            <div style={styles.menuLabelWrapper} onClick={() => itemExpand(parentIndex)}>
                                {menuOption.label}
                                <Dropdown style={styles.menuLabelIcon} />
                            </div>
                            <div style={menuOption.expand ? styles.expand : styles.minimize}>
                                {menuOption.options.map((option, childIndex) =>                                     
                                    <div key={childIndex}>                       
                                        <DropdownItem styles={styles.optionWrapper} onClick={() => selectOption(option)}>
                                            <div style={styles.primaryOption}>{option.primaryOption}</div>
                                            {option.secondaryOption && <div style={styles.secondaryOption}>{option.secondaryOption}</div>}
                                        </DropdownItem>  
                                        <hr style={styles.bottomBorder} />  
                                    </div>
                                )}
                            </div>
                        </div>                                                                            
                    )}    
                </div>
            </div>
        </div>
    );
};